import { useRolodexFileLS } from 'features/rolodex'
import { useMemo } from 'react'

/** Paths to tileset.json files for a single layer (e.g., rgb, day_ir, night_ir) */
export interface LayerTilesetPaths {
  /** Layer name (e.g., 'rgb', 'day_ir', 'night_ir') */
  layerName: string
  /**
   * Relative paths from 3d_tiles folder to each tileset.
   * For simple case: ['rgb'] (tileset at 3d_tiles/rgb/tileset.json)
   * For subfolders: ['rgb/BlockA', 'rgb/BlockB'] (multiple tilesets)
   */
  tilesetPaths: string[]
}

/**
 * Parses tileset.json file paths and groups them by layer.
 * Handles both simple structure (3d_tiles/rgb/tileset.json) and
 * nested structure (3d_tiles/rgb/BlockA/tileset.json).
 */
function parseLayerTilesets(paths: string[]): Map<string, LayerTilesetPaths> {
  const layerMap = new Map<string, LayerTilesetPaths>()

  // Pattern for direct layer tileset: 3d_tiles/{layer}/tileset.json
  const directPattern = /^3d_tiles\/([^/]+)\/tileset\.json$/
  // Pattern for nested subfolder tileset: 3d_tiles/{layer}/{subfolder}/tileset.json
  const nestedPattern = /^3d_tiles\/([^/]+)\/([^/]+)\/tileset\.json$/

  for (const path of paths) {
    const directMatch = path.match(directPattern)
    if (directMatch) {
      const layerName = directMatch[1]
      // Direct tileset at layer root
      if (!layerMap.has(layerName)) {
        layerMap.set(layerName, { layerName, tilesetPaths: [layerName] })
      }
      continue
    }

    const nestedMatch = path.match(nestedPattern)
    if (nestedMatch) {
      const layerName = nestedMatch[1]
      const subfolder = nestedMatch[2]
      const tilesetPath = `${layerName}/${subfolder}`

      if (layerMap.has(layerName)) {
        const existing = layerMap.get(layerName)!
        // Only add if we haven't added a direct tileset for this layer
        if (!existing.tilesetPaths.includes(layerName)) {
          existing.tilesetPaths.push(tilesetPath)
        }
      } else {
        layerMap.set(layerName, { layerName, tilesetPaths: [tilesetPath] })
      }
    }
  }

  return layerMap
}

export const useHasKestrel3DTiles = (kestrelModelId: string | undefined) => {
  const { fileRefs, isLoading, error } = useRolodexFileLS(
    kestrelModelId
      ? {
          entry_id: kestrelModelId,
          path: '**/tileset.json',
        }
      : undefined
  )

  const tilesFolderInfo = useMemo(() => {
    if (error) {
      return {
        has3DTiles: false,
        availableLayers: [] as string[],
        layerTilesets: new Map<string, LayerTilesetPaths>(),
      }
    }

    if (!fileRefs || fileRefs.length === 0) {
      return {
        has3DTiles: false,
        availableLayers: [] as string[],
        layerTilesets: new Map<string, LayerTilesetPaths>(),
      }
    }

    const paths = fileRefs.map((ref) => ref.path)
    const layerTilesets = parseLayerTilesets(paths)
    const availableLayers = Array.from(layerTilesets.keys())
    const has3DTiles = availableLayers.length > 0

    return { has3DTiles, availableLayers, layerTilesets }
  }, [fileRefs, error])

  return { ...tilesFolderInfo, isLoading }
}
